import { Component, OnInit, OnDestroy } from "@angular/core";
import { PortalAdminService } from "../../services/portal-admin-service/portal-adimin.service";
import { Customer } from "../../models/customer.model";
import { Permission } from "../../models/permission.model";
import {
  UserPermission,
  SpecialProgram,
} from "../../models/userPermission.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import {
  distinctUntilChanged,
  debounceTime,
  takeUntil,
  switchMap,
} from "rxjs/operators";
import { Subject, Observable } from "rxjs";
import { Subscription } from "rxjs/internal/Subscription";

@Component({
    selector: "pa-rewards-permission",
    templateUrl: "./rewards-permission.component.html",
    styleUrls: ["./rewards-permission.component.scss"],
    standalone: false
})
export class RewardsPermissionComponent implements OnInit, OnDestroy {
  isEdit = false;
  isLoading: boolean;

  companies: Customer[];
  company;
  branches: Customer[];
  branch: Customer;
  permissions: Permission[];
  userIndex: number;
  users: UserPermission[];
  searchUsersArry: UserPermission[];
  companyHoldingId: string;
  userSearch;
  noResults = false;
  userPermissionsIsDone: boolean;
  noUserPermissions: boolean;
  branchPermissionsIsDone: boolean;
  noBranchPermissions: boolean;

  searchSubscriptions: Subscription[] = [];

  public keyUp = new Subject<KeyboardEvent>();
  private onDestroy$ = new Subject<void>();
  private branches$: Subject<Observable<Customer[]>> = new Subject();
  private userPermissions$: Subject<Observable<UserPermission[]>> =
    new Subject();
  private branchPermissions$: Subject<Observable<Permission[]>> = new Subject();

  constructor(
    private portalAdminService: PortalAdminService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.keyUp
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((event: KeyboardEvent) => {
        if (
          !!this.company &&
          event.code !== "ArrowDown" &&
          event.code !== "ArrowUp" &&
          event.code !== "ArrowLeft" &&
          event.code !== "ArrowRight" &&
          event.code !== "Enter" &&
          event.code !== "Tab"
        ) {
          this.getCompanyMatchesList(event);
        }
      });
  }

  ngOnInit() {
    this.branches$
      .pipe(
        switchMap((x) => x),
        takeUntil(this.onDestroy$)
      )
      .subscribe(
        (branches: Customer[]) => {
          this.isLoading = false;
          if (branches.length > 0) {
            this.branches = branches.sort((x, y) => {
              if (x.primaryState < y.primaryState) {
                return -1;
              } else if (x.primaryState > y.primaryState) {
                return 1;
              } else {
                return x.primaryCity < y.primaryCity ? -1 : 1;
              }
            });

            const storedSelectedBranch = JSON.parse(
              localStorage.getItem("selectedBranch")
            );
            const selectedBranch = storedSelectedBranch
              ? this.branches.find(
                  (x) =>
                    x.accountId.toLowerCase() ===
                    storedSelectedBranch.accountId.toLowerCase()
                )
              : null;
            this.branch = selectedBranch ? selectedBranch : this.branches[0];
            this.selectBranch();
          }
        },
        (err: HttpErrorResponse) => {
          this.isLoading = false;
          console.log(err);
          if (err.status === 403 || err.status === 401) {
            this.router.navigate(["/access-denied"]);
          } else {
            this.router.navigate(["/error"]);
          }
        }
      );

    this.userPermissions$
      .pipe(
        switchMap((x) => x),
        takeUntil(this.onDestroy$)
      )
      .subscribe(
        (permissions: UserPermission[]) => {
          if (permissions.length <= 0) {
            this.noUserPermissions = true;
          } else {
            this.noUserPermissions = false;
            this.users = permissions;
            this.searchUsersArry = permissions;
          }

          this.userPermissionsIsDone = true;
          this.isLoading = !this.branchPermissionsIsDone;
        },
        (err: HttpErrorResponse) => {
          this.isLoading = false;
          console.log(err);
          if (err.status === 403 || err.status === 401) {
            this.router.navigate(["/access-denied"]);
          } else {
            this.router.navigate(["/error"]);
          }
        }
      );

    this.branchPermissions$
      .pipe(
        switchMap((x) => x),
        takeUntil(this.onDestroy$)
      )
      .subscribe(
        (permissions: Permission[]) => {
          this.permissions = [];

          if (permissions.length > 0) {
            for (const permiss of permissions.filter((x) => x.isSecured)) {
              switch (permiss.code.toLowerCase()) {
                case "clbintegrator_sso":
                case "clblocksmith_sso":
                case "clbsinglefamily_sso":
                  this.permissions.push(permiss);
                  break;
              }
            }
          }

          this.noBranchPermissions = this.permissions.length > 0 ? false : true;
          this.branchPermissionsIsDone = true;
          this.isLoading = !this.userPermissionsIsDone;
        },
        (err: HttpErrorResponse) => {
          this.isLoading = false;
          console.log(err);
          if (err.status === 403 || err.status === 401) {
            this.router.navigate(["/access-denied"]);
          } else {
            this.router.navigate(["/error"]);
          }
        }
      );

    const storedSelectedCompany = localStorage.getItem("selectedCompany");
    if (storedSelectedCompany) {
      this.company = JSON.parse(storedSelectedCompany);
      this.selectCompany();
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.unsubscribe();
  }

  getCompanyMatchesList(search) {
    this.isLoading = true;
    this.searchSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.searchSubscriptions.push(
      this.portalAdminService
        .getCustomers(this.company)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((companyRes: Customer[]) => {
          this.companies = companyRes;
          this.noResults = false;
          if (companyRes.length === 0) {
            this.isLoading = false;
            this.noResults = true;
          }
        })
    );
  }

  searchEvent(event: KeyboardEvent) {
    if (event.code === "Enter" || event.code === "Tab") {
      this.selectCompany();
    } else {
      this.keyUp.next(event);
    }
  }

  selectCompany() {
    if (this.company) {
      localStorage.setItem("selectedCompany", JSON.stringify(this.company));
      const tempCompany = { ...this.company };
      this.companyHoldingId = this.company.holdingAccountId;
      this.company = tempCompany.customerName;
      this.branches = [];
      this.branch = undefined;

      this.isLoading = true;
      this.userPermissionsIsDone = false;
      this.branchPermissionsIsDone = false;
      this.noUserPermissions = false;
      this.noBranchPermissions = false;
      this.branches$.next(
        this.portalAdminService.getBranches(this.companyHoldingId)
      );
    }
  }

  selectBranch() {
    if (this.branch) {
      localStorage.setItem("selectedBranch", JSON.stringify(this.branch));

      this.isLoading = true;
      this.userPermissionsIsDone = false;
      this.branchPermissionsIsDone = false;
      this.noUserPermissions = false;
      this.noBranchPermissions = false;
      this.userPermissions$.next(
        this.portalAdminService.getBranchUserPermissions(this.branch.accountId)
      );
      this.branchPermissions$.next(
        this.portalAdminService.getPermissions(this.branch.accountId)
      );
    }
  }

  contentLoaded() {
    if (
      this.branchPermissionsIsDone === true &&
      this.userPermissionsIsDone === true &&
      this.noUserPermissions === false &&
      this.noBranchPermissions === false
    ) {
      return true;
    } else {
      return false;
    }
  }

  isChecked(userId: number, permission: Permission, index: number): boolean {
    if (this.users[index].specialPrograms.length === 0) {
      return false;
    } else {
      if (userId === this.users[index].userId) {
        for (const special of this.users[index].specialPrograms) {
          if (special.specialProgramId === permission.databaseId) {
            return true;
          }
        }
        return false;
      }
    }
  }

  searchUsers(event) {
    const results = this.searchUsersArry.filter((user) => {
      const userName = `${user.firstName} ${user.lastName}`;
      if (userName.toUpperCase().includes(this.userSearch.toUpperCase())) {
        return true;
      } else {
        return false;
      }
    });
    this.users = results;
  }

  clearSearch() {
    this.userSearch = "";
    this.users = this.searchUsersArry;
  }

  checkboxSelected(
    event: MatCheckboxChange,
    permission: Permission,
    selectedUser: UserPermission
  ) {
    if (event.checked === true) {
      for (const user of this.users) {
        if (user.auth0Identifier === selectedUser.auth0Identifier) {
          const specialProgramObj: SpecialProgram = {
            userAccessId: 0,
            specialProgramId: permission.databaseId,
            programName: permission.name,
            programCode: permission.code,
            role: "user",
          };
          user.specialPrograms.push(specialProgramObj);
        }
      }
    } else if (event.checked === false) {
      for (const user of this.users) {
        if (user.auth0Identifier === selectedUser.auth0Identifier) {
          user.specialPrograms.forEach((userSpecialProgram, index) => {
            if (permission.databaseId === userSpecialProgram.specialProgramId) {
              user.specialPrograms.splice(index, 1);
            }
          });
        }
      }
    }
  }

  editUser(index: number) {
    this.userIndex = index;
    this.isEdit = true;
  }

  saveUserChanges(index: number, userSave: UserPermission) {
    this.isLoading = true;
    if (userSave.userId === 0) {
      for (const user of this.users) {
        if (user.auth0Identifier === userSave.auth0Identifier) {
          this.portalAdminService
            .addBranchUserPermissions(this.branch.accountId, user)
            .subscribe((updateRes) => {
              this.portalAdminService
                .getBranchUserPermissions(this.branch.accountId)
                .subscribe((userRes: UserPermission[]) => {
                  this.users = userRes;
                  this.isLoading = false;
                  this.snackBar.open("User Permissions Updated", "", {
                    duration: 2250,
                  });
                });
            });
        }
      }
    } else if (userSave.userId !== 0) {
      for (const user of this.users) {
        if (user.auth0Identifier === userSave.auth0Identifier) {
          this.portalAdminService
            .updateBranchUserPermissions(this.branch.accountId, user)
            .subscribe((updateRes) => {
              this.portalAdminService
                .getBranchUserPermissions(this.branch.accountId)
                .subscribe((userRes: UserPermission[]) => {
                  this.users = userRes;
                  this.isLoading = false;
                  this.snackBar.open("User Permissions Updated", "", {
                    duration: 2250,
                  });
                });
            });
        }
      }
    }
    this.userIndex = -1;
    this.isEdit = false;
  }
}
